import { useEffect, useState } from 'react';
import cls from 'classnames';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import getQuery from '~/utils/get-query';
import TestSymbol from '../test-symbol';
import { useAtomValue } from 'jotai';
import { PolicyPath, Lemon8Host } from 'shared/constants';
import { $LegalRegion, $Location } from 'shared/atoms';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { Language, Region, getRegionIDC } from '@lemon8/web-app-shared/i18n';
import { handleUrl } from '~/utils/webview-url';

import 'intersection-observer';

import '~/styles/policy.scss';
import '~/styles/policy-inapp.scss';
import '~/styles/policy-mobile.scss';

import './index.scss';

const getDomain = (isTestEnv: boolean, region: Region) => {
  if (isTestEnv) {
    if (getRegionIDC(region) === 'sg') {
      return 'lemon8-sg.tiktok-row.net';
    }

    return 'lemon8-va.tiktok-row.net';
  }

  return 'www.lemon8-app.com';
};

const filterURL = (
  raw: string,
  legalRegion: Region,
  regionLanguage: Language,
  reportUrl: string,
  isTestEnv: boolean,
): string => {
  const officialDomain = getDomain(isTestEnv, legalRegion);

  const reportUrlQueryStr = reportUrl ? `&report_url=${encodeURIComponent(reportUrl)}` : '';
  const copyrightReportUrl = `https://${officialDomain}/h5/feoffline/report/email-verify/input?region=${legalRegion}&language=${regionLanguage}${reportUrlQueryStr}&report_type=copyright`;
  const trademarkReportUrl = `https://${officialDomain}/h5/feoffline/report/email-verify/input?region=${legalRegion}&language=${regionLanguage}${reportUrlQueryStr}&report_type=trademark`;
  const adequacyDecisionsUrl =
    'https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/adequacy-decisions_en';

  // can not use schema here, because ReactMarkdown cannot handle schema with <a href={schema}></a>
  // muse use whole url, otherwise ReactMarkdown handle schema maybe error
  return Object.keys(PolicyPath).reduce((str: string, k) => {
    return str
      .replace(
        new RegExp(`LEGAL_${k}`, 'g'),
        `https://${officialDomain}${
          PolicyPath[k as keyof typeof PolicyPath]
        }?region=${legalRegion}&language=${regionLanguage}`,
      )
      .replace(new RegExp('MAIL_LEMON8', 'g'), 'mailto:IP@lemon8-app.com')
      .replace(new RegExp('REPORT_INFRINGEMENT_COPYRIGHT', 'g'), copyrightReportUrl)
      .replace(new RegExp('REPORT_INFRINGEMENT_TRADEMARK', 'g'), trademarkReportUrl)
      .replace(new RegExp('ADEQUACY_DECISIONS', 'g'), adequacyDecisionsUrl);
  }, raw);
};

export type TLegalMarkdownSrc = { [key in Region]: any };

export default function MarkdownNode(props: {
  isMobile?: boolean;
  isInApp?: boolean;
  src: string | TLegalMarkdownSrc;
}): JSX.Element {
  const { isMobile = false, isInApp = false, src = '' } = props;

  const { region: legalRegion } = useAtomValue($LegalRegion);
  const { language: regionLanguage } = useAtomValue($Locale);
  const { hasNav = true } = useAtomValue($Location);

  const [reportUrl, setReportUrl] = useState<string>('');

  // 区分内测和线上域名，防止其他端同学挂错链接
  const [isTestEnv, setIsTestEnv] = useState<boolean>(false);

  let content = '';
  if (typeof src !== 'string') {
    content = src[legalRegion];
  } else {
    content = src;
  }

  useEffect(() => {
    const urlHost = document.domain;
    if (
      urlHost.includes(Lemon8Host.TEST_ROW_SURFIX) ||
      urlHost.includes(Lemon8Host.TEST_VA_PREFIX) ||
      urlHost.includes(Lemon8Host.TEST_SG_PREFIX)
    ) {
      setIsTestEnv(true);
    }
    let observer: null | IntersectionObserver = null;
    const titleElement = document.querySelector('.document-container h1');
    const docTitle = titleElement?.textContent || '';
    const original_report_url = getQuery('report_url') || '';
    const report_url = original_report_url ? decodeURIComponent(original_report_url) : '';
    setReportUrl(report_url);
    const handleIntersect = (entries: any[]) => {
      entries.forEach(async (entry) => {
        if (entry.intersectionRatio > 0.0) {
          if (isInApp) {
            const JSB = await import('@bridge/sharee');
            JSB.view.setTitleVisible({ title: '' });
          }
        } else {
          if (isInApp) {
            const JSB = await import('@bridge/sharee');
            JSB.view.setTitleVisible({ title: docTitle });
          }
        }
      });
    };
    const createObserver = () => {
      const options = {
        threshold: 0.0,
      };
      const innerObserver = new IntersectionObserver(handleIntersect, options);
      if (titleElement) {
        innerObserver.observe(titleElement);
      }
      return innerObserver;
    };
    observer = createObserver();
    return () => {
      observer && observer?.disconnect();
    };
  }, []);

  return (
    <>
      {isTestEnv && <TestSymbol />}
      <ReactMarkdown
        className={cls('document-container', {
          'platform-inapp': isInApp,
          'platform-mobile': isMobile,
          'has-nav': !isInApp && hasNav,
        })}
        rehypePlugins={[rehypeRaw as any]}
        remarkPlugins={[remarkGfm as any]}
        components={{
          // must handle the url when in app, other the webview back behavior maybe error
          a: (props) => {
            const { node, href = '', children, ...rest } = props;

            return (
              <a {...rest} href={handleUrl(href, isInApp)}>
                {children}
              </a>
            );
          },
        }}
      >
        {filterURL(content, legalRegion, regionLanguage, reportUrl, isTestEnv)}
      </ReactMarkdown>
    </>
  );
}
